import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Paper,
  Divider,
  Grid,
  Card,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  AutoAwesome as AutoAwesomeIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { walletService, AutoTopUpConfig } from '../../../../services/WalletService'
import { StyledButton } from '../../styles/StyledComponents'

interface AutoTopUpDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  companyId: string
  currentConfig: AutoTopUpConfig
}

export const AutoTopUpDialog: React.FC<AutoTopUpDialogProps> = ({
  open,
  onClose,
  onSuccess,
  companyId,
  currentConfig,
}) => {
  const theme = useTheme()
  const [enabled, setEnabled] = React.useState(currentConfig.enabled)
  const [threshold, setThreshold] = React.useState(currentConfig.threshold.toString())
  const [amount, setAmount] = React.useState(currentConfig.amount.toString())
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState(false)

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setEnabled(currentConfig.enabled)
      setThreshold(currentConfig.threshold.toString())
      setAmount(currentConfig.amount.toString())
      setError(null)
      setSuccess(false)
    }
  }, [open, currentConfig])

  const handleThresholdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setThreshold(value)
      setError(null)
    }
  }

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value)
      setError(null)
    }
  }

  const handleSubmit = async () => {
    try {
      setError(null)
      setLoading(true)

      const thresholdNum = parseFloat(threshold)
      const amountNum = parseFloat(amount)

      // Validate inputs
      if (enabled) {
        if (!threshold || thresholdNum < 0) {
          setError('Threshold must be a positive number')
          return
        }
        if (!amount || amountNum < 5) {
          setError('Auto top-up amount must be at least $5.00')
          return
        }
        if (amountNum > 10000) {
          setError('Auto top-up amount cannot exceed $10,000.00')
          return
        }
      }

      const config: AutoTopUpConfig = {
        enabled,
        threshold: thresholdNum,
        amount: amountNum
      }

      const result = await walletService.configureAutoTopUp(companyId, config)
      
      if (!result.success) {
        setError(result.error || 'Failed to configure auto top-up')
        return
      }

      setSuccess(true)
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 2000)

    } catch (error: any) {
      console.error('Error configuring auto top-up:', error)
      setError(error.message || 'An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.shadows[10],
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'action.hover',
        backgroundImage: 'none',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Auto Top-Up Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Automatically manage your wallet balance
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2, pt: 2.5 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
              Configuration Updated!
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Your auto top-up settings have been saved successfully.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Enable/Disable Switch */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                mt: 1,
                p: 2,
                bgcolor: enabled ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                backgroundImage: 'none',
                border: '1px solid',
                borderColor: enabled ? alpha(theme.palette.primary.main, 0.25) : 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: enabled ? alpha(theme.palette.primary.main, 0.14) : 'action.hover',
                  backgroundImage: 'none',
                  borderColor: enabled ? alpha(theme.palette.primary.main, 0.4) : 'divider',
                  boxShadow: theme.shadows[2],
                },
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Enable Auto Top-Up
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Automatically add funds when balance falls below threshold
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            {enabled && (
              <>
                {/* Configuration Fields */}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Low Balance Threshold"
                      type="text"
                      value={threshold}
                      onChange={handleThresholdChange}
                      placeholder="10.00"
                      helperText="Balance level that triggers auto top-up"
                      InputProps={{
                        startAdornment: <Typography sx={{ color: 'text.secondary', mr: 1 }}>$</Typography>
                      }}
                      sx={{
                        '& .MuiInputLabel-root': { 
                          color: 'text.secondary',
                          marginLeft: '16px'
                        },
                        '& .MuiOutlinedInput-root': { 
                          color: 'text.primary',
                          paddingLeft: '16px',
                          '& .MuiInputBase-input': { 
                            color: 'text.primary',
                            paddingLeft: '12px'
                          }
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: 'divider',
                          '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.35) },
                          '&.Mui-focused': { borderColor: 'primary.main' }
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Top-Up Amount"
                      type="text"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder="50.00"
                      helperText="Amount to add when triggered"
                      InputProps={{
                        startAdornment: <Typography sx={{ color: 'text.secondary', mr: 1 }}>$</Typography>
                      }}
                      sx={{
                        '& .MuiInputLabel-root': { 
                          color: 'text.secondary',
                          marginLeft: '16px'
                        },
                        '& .MuiOutlinedInput-root': { 
                          color: 'text.primary',
                          paddingLeft: '16px',
                          '& .MuiInputBase-input': { 
                            color: 'text.primary',
                            paddingLeft: '12px'
                          }
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: 'divider',
                          '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.35) },
                          '&.Mui-focused': { borderColor: 'primary.main' }
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Information Card */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    backgroundImage: 'none',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      backgroundImage: 'none',
                      borderColor: alpha(theme.palette.primary.main, 0.35),
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <SettingsIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
                        How Auto Top-Up Works
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        • When your wallet balance falls below ${threshold || '10.00'}, we'll automatically add ${amount || '50.00'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        • Payment will be charged to your default payment method
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        • You'll receive an email notification when auto top-up occurs
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Warning Card */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    backgroundImage: 'none',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.warning.main, 0.14),
                      backgroundImage: 'none',
                      borderColor: alpha(theme.palette.warning.main, 0.4),
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <WarningIcon sx={{ color: 'warning.main', fontSize: 20, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'warning.dark', fontWeight: 600, mb: 1 }}>
                        Important Notes
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        • Auto top-up requires an active payment method
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        • You can disable auto top-up at any time
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        • Failed payments will be retried automatically
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        bgcolor: 'action.hover',
        backgroundImage: 'none',
        borderTop: '1px solid',
        borderColor: 'divider',
        padding: '16px 24px'
      }}>
        <StyledButton 
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ 
            color: 'text.secondary', 
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              backgroundImage: 'none',
            }
          }}
        >
          {success ? 'Close' : 'Cancel'}
        </StyledButton>
        {!success && (
          <StyledButton 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || (enabled && (!threshold || !amount))}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'primary.contrastText' }} /> : <AutoAwesomeIcon />}
            sx={{
              bgcolor: 'primary.main',
              backgroundImage: 'none',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.light',
                backgroundImage: 'none',
              },
              '&:disabled': {
                bgcolor: alpha(theme.palette.primary.main, 0.3),
                backgroundImage: 'none',
                color: 'text.disabled'
              }
            }}
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </StyledButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
